import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  TextField,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import { useLazyListShipmentForBulkCancelQuery } from "store/services/reportApi";
import { exportExcelFile } from "utils/exportExcelFile";
import moment from "moment";
import { FileDownloadOutlined } from "@mui/icons-material";

const EXPORT_DATA_INITIAL_STATE = {
  expiryFilter: "all",
  dateRange: [],
};

const ExportBulkCancelShipmentReport = ({ isBusy = false }) => {
  const [showDialog, setShowDialog] = React.useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: EXPORT_DATA_INITIAL_STATE,
  });

  const selectedFilter = watch("expiryFilter");
  const [fetchShipment, { isLoading: isFetching }] = useLazyListShipmentForBulkCancelQuery();

  const handleDownload = async (data) => {
    try {
      let params = {};
      let fileNameParts = ["Bulk_Cancel_Shipment_Report"];

      if (data.expiryFilter === "custom" && data.dateRange?.length === 2) {
        params = {
          changeLogFromDate: moment(data.dateRange[0]).format("YYYY-MM-DD"),
          changeLogToDate: moment(data.dateRange[1]).format("YYYY-MM-DD"),
        };
        fileNameParts.push(
          `${moment(data.dateRange[0]).format("MMM_DD")}_to_${moment(data.dateRange[1]).format("MMM_DD")}`
        );
      } else if (data.expiryFilter === "week" || data.expiryFilter === "month") {
        const days = data.expiryFilter === "week" ? 7 : 30;
        const toDate = moment();
        const fromDate = moment().subtract(days, "days");
        params = {
          changeLogFromDate: fromDate.format("YYYY-MM-DD"),
          changeLogToDate: toDate.format("YYYY-MM-DD"),
        };
        fileNameParts.push(data.expiryFilter === "week" ? "1_Week" : "1_Month");
      }
      // For "all" filter, no date range parameters are added to fetch all records

      const responseData = await fetchShipment(params).unwrap();

      if (!responseData?.data || Object.keys(responseData.data).length === 0) {
        toast.info("No shipments found for the selected criteria.");
        return;
      }

      const formattedData = Object.values(responseData.data).map((row, index) => ({
        SN: index + 1,
        "Shipment ID": row.shipmentId || "N/A",
        "Added User": row?.changeLogs?.[0]?.addedUser?.name || "N/A",
        "Added Time": row?.changeLogs?.[0]?.createdAt
          ? moment(row.changeLogs[0].createdAt).format("YYYY-MM-DD HH:mm:ss")
          : "N/A",
        "Reason": row?.changeLogs?.[0]?.changeLogs || "N/A",
      }));

      const cleanFileNameParts = fileNameParts
        .map((part) => part.replace(/[^a-zA-Z0-9_]/g, "").trim())
        .filter((part) => part.length > 0);

      const fileName = cleanFileNameParts.join("_");

      exportExcelFile({
        sheetData: formattedData,
        fileName: fileName,
      });

      toast.success("Report downloaded successfully");
      handleReset();
    } catch (error) {
      toast.error("Error downloading report: " + (error.message || "Unknown error"));
    }
  };

  const handleReset = () => {
    setShowDialog(false);
    reset(EXPORT_DATA_INITIAL_STATE);
  };

  return (
    <>
      <Tooltip title="Export Excel" arrow>
        <IconButton
          color="success"
          onClick={() => setShowDialog(true)}
          disabled={isBusy}
        >
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Bulk Cancel Shipment Report"
        dialogIcon={() => <FileDownloadOutlined color="success" />}
        confirmText="Download"
        confirmDisabled={isFetching || isBusy}
      >
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Change Log Date Range</FormLabel>
          <Controller
            name="expiryFilter"
            control={control}
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel
                  value="week"
                  control={<Radio />}
                  label="1 Week"
                />
                <FormControlLabel
                  value="month"
                  control={<Radio />}
                  label="1 Month"
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio />}
                  label="Custom Date Range"
                />
              </RadioGroup>
            )}
          />
        </FormControl>

        {selectedFilter === "custom" && (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <Controller
                name="dateRange[0]"
                control={control}
                rules={{
                  validate: (value) => {
                    if (selectedFilter === "custom" && !value) {
                      return "Please select a start date";
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <DesktopDatePicker
                    label="Start Date"
                    inputFormat="MM/DD/YYYY"
                    value={value || null}
                    onChange={(date) => onChange(date)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="dateRange[1]"
                control={control}
                rules={{
                  validate: (value) => {
                    if (selectedFilter === "custom" && !value) {
                      return "Please select an end date";
                    }
                    const startDate = watch("dateRange[0]");
                    if (
                      selectedFilter === "custom" &&
                      startDate &&
                      value &&
                      moment(value).isBefore(moment(startDate))
                    ) {
                      return "End date must be after start date";
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <DesktopDatePicker
                    label="End Date"
                    inputFormat="MM/DD/YYYY"
                    value={value || null}
                    onChange={(date) => onChange(date)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                    shouldDisableDate={(date) =>
                      watch("dateRange[0]") && date < moment(watch("dateRange[0]"))
                    }
                    disabled={!watch("dateRange[0]")}
                  />
                )}
              />
            </Box>
          </LocalizationProvider>
        )}
      </AppConfirmDialog>
    </>
  );
};

export default ExportBulkCancelShipmentReport;